/**
 * Clerk → `sign_up` mapping. Pure so the payload rules can be asserted
 * without mounting Clerk: the hook is thin glue that snapshots the listener
 * emission and delegates every decision here.
 *
 * Verified against this app's stack (`@clerk/clerk-react` ^5.59.0 → 5.61.3,
 * `@clerk/shared` 3.47.7 `Resources` / `SignUpResource` / `SignInResource`):
 * - `addListener` emits `{ client, session?, user?, organization? }`.
 * - `SignUp.status` is `missing_requirements` | `complete` | `abandoned`.
 * - `SignUp.createdSessionId` is documented as populated **only when the
 *   sign-up is complete** — so it is not an independent live-path signal.
 * - `SignIn.createdSessionId` is likewise null unless `SignIn.status` is
 *   `complete`. After email_code, Clerk may finish via SignIn + `setActive`
 *   and reset SignUp before we ever observe `complete`.
 *
 * Fire when **any** of these hold:
 * 1. SignUp `status === "complete"` or SignUp `createdSessionId` (cadastro
 *    that Clerk still reports as a finished SignUp).
 * 2. A `/sign-up` attempt was marked in this tab **and** the first signed-in
 *    hop arrives (`session` present, or SignIn `createdSessionId`).
 *
 * Attempt is marked only when the visitor is on `/sign-up` and Clerk already
 * has an identifier (SignUp email/phone/username, unverified fields, or
 * SignIn identifier with `email_code` / `email_link`). A cold view of
 * `/sign-up` has none of those and must not emit. `/sign-in` clears the
 * attempt so a returning user who signs in does not inherit a leftover flag.
 *
 * Idempotency is sessionStorage, not a Clerk user id in the GA4 payload —
 * no email, name, CPF, or user id is sent.
 */

import type { AnalyticsEvent } from "./analytics";

/** sessionStorage flag so a complete emission plus the post-signup redirect cannot double-fire. */
export const SIGN_UP_EVENT_STORAGE_KEY = "ruralops:sign_up_event";

/** sessionStorage flag: this tab started trial cadastro on `/sign-up`. */
export const SIGN_UP_ATTEMPT_STORAGE_KEY = "ruralops:sign_up_attempted";

/** Optional GA4 `method` inferred while the attempt was still visible. */
const SIGN_UP_ATTEMPT_METHOD_STORAGE_KEY = "ruralops:sign_up_attempt_method";

export type SignUpSnapshot = {
	status?: string | null;
	oauthStrategy?: string | null;
	emailStrategy?: string | null;
	emailVerified?: boolean;
	externalProvider?: string | null;
	pathname?: string;
	/** Present on SignUp only when Clerk considers the sign-up complete. */
	signUpCreatedSessionId?: boolean;
	/** Identifier exists; the address itself is never copied into the snapshot. */
	signUpHasIdentifier?: boolean;
	signUpUnverifiedCount?: number;
	signInStatus?: string | null;
	signInCreatedSessionId?: boolean;
	signInHasIdentifier?: boolean;
	signInFirstFactorStrategy?: string | null;
	hasActiveSession?: boolean;
};

export type ClerkSignUpResources = {
	client?: {
		signUp?: {
			status?: string | null;
			createdSessionId?: string | null;
			emailAddress?: string | null;
			phoneNumber?: string | null;
			username?: string | null;
			unverifiedFields?: string[] | null;
			verifications?: {
				externalAccount?: { strategy?: string | null } | null;
				emailAddress?: {
					strategy?: string | null;
					status?: string | null;
				} | null;
			} | null;
		} | null;
		signIn?: {
			status?: string | null;
			createdSessionId?: string | null;
			identifier?: string | null;
			firstFactorVerification?: { strategy?: string | null } | null;
		} | null;
	} | null;
	session?: { id?: string | null } | null;
	user?: {
		externalAccounts?: Array<{ provider?: string | null }>;
	} | null;
};

export type SignUpAnalyticsStorage = Pick<
	Storage,
	"getItem" | "setItem" | "removeItem"
>;

function isSignUpPath(pathname: string): boolean {
	return pathname === "/sign-up" || pathname.startsWith("/sign-up/");
}

function isSignInPath(pathname: string): boolean {
	return pathname === "/sign-in" || pathname.startsWith("/sign-in/");
}

function isEmailCodeStrategy(strategy: string | null | undefined): boolean {
	return strategy === "email_code" || strategy === "email_link";
}

export function snapshotFromClerkResources(
	resources: ClerkSignUpResources,
	pathname: string,
): SignUpSnapshot {
	const signUp = resources.client?.signUp;
	const signIn = resources.client?.signIn;
	const email = signUp?.verifications?.emailAddress;
	const external = signUp?.verifications?.externalAccount;
	return {
		status: signUp?.status ?? null,
		oauthStrategy: external?.strategy ?? null,
		emailStrategy: email?.strategy ?? null,
		emailVerified: email?.status === "verified",
		externalProvider: resources.user?.externalAccounts?.[0]?.provider ?? null,
		pathname,
		signUpCreatedSessionId: Boolean(signUp?.createdSessionId),
		signUpHasIdentifier: Boolean(
			signUp?.emailAddress || signUp?.phoneNumber || signUp?.username,
		),
		signUpUnverifiedCount: signUp?.unverifiedFields?.length ?? 0,
		signInStatus: signIn?.status ?? null,
		signInCreatedSessionId: Boolean(signIn?.createdSessionId),
		signInHasIdentifier: Boolean(signIn?.identifier),
		signInFirstFactorStrategy:
			signIn?.firstFactorVerification?.strategy ?? null,
		hasActiveSession: Boolean(resources.session),
	};
}

/**
 * Optional `method` for GA4's recommended `sign_up` event. Unknown strategies
 * are dropped rather than reported as a fabricated bucket.
 */
export function methodFromSignUpSnapshot(
	snapshot: SignUpSnapshot,
): string | undefined {
	const oauth = snapshot.oauthStrategy;
	if (typeof oauth === "string" && oauth.startsWith("oauth_")) {
		const provider = oauth.slice("oauth_".length);
		return provider.length > 0 ? provider : undefined;
	}
	if (
		typeof snapshot.externalProvider === "string" &&
		snapshot.externalProvider.length > 0
	) {
		return snapshot.externalProvider;
	}
	if (
		snapshot.emailVerified === true ||
		isEmailCodeStrategy(snapshot.emailStrategy) ||
		isEmailCodeStrategy(snapshot.signInFirstFactorStrategy)
	) {
		return "email";
	}
	return undefined;
}

export function shouldMarkSignUpAttempt(snapshot: SignUpSnapshot): boolean {
	if (!isSignUpPath(snapshot.pathname ?? "")) return false;
	if (snapshot.signUpHasIdentifier) return true;
	if ((snapshot.signUpUnverifiedCount ?? 0) > 0) return true;
	if (
		snapshot.signInHasIdentifier &&
		isEmailCodeStrategy(snapshot.signInFirstFactorStrategy)
	) {
		return true;
	}
	return false;
}

export function isCompletedTrialSignUp(
	snapshot: SignUpSnapshot,
	attemptMarked: boolean,
): boolean {
	if (snapshot.status === "complete") return true;
	if (snapshot.signUpCreatedSessionId) return true;
	if (!attemptMarked) return false;
	if (snapshot.signInCreatedSessionId) return true;
	if (snapshot.signInStatus === "complete") return true;
	if (snapshot.hasActiveSession) return true;
	return false;
}

export function completedSignUpEvent(
	snapshot: SignUpSnapshot,
	attemptMarked = false,
): AnalyticsEvent | null {
	if (!isCompletedTrialSignUp(snapshot, attemptMarked)) return null;
	const method = methodFromSignUpSnapshot(snapshot);
	return method ? { event: "sign_up", method } : { event: "sign_up" };
}

export function trackCompletedSignUpOnce(
	snapshot: SignUpSnapshot,
	deps: {
		track: (event: AnalyticsEvent) => void;
		storage: SignUpAnalyticsStorage;
	},
): boolean {
	const pathname = snapshot.pathname ?? "";
	try {
		if (isSignInPath(pathname)) {
			deps.storage.removeItem(SIGN_UP_ATTEMPT_STORAGE_KEY);
			deps.storage.removeItem(SIGN_UP_ATTEMPT_METHOD_STORAGE_KEY);
			return false;
		}
		if (shouldMarkSignUpAttempt(snapshot)) {
			deps.storage.setItem(SIGN_UP_ATTEMPT_STORAGE_KEY, "1");
			const attemptMethod = methodFromSignUpSnapshot(snapshot);
			if (attemptMethod) {
				deps.storage.setItem(SIGN_UP_ATTEMPT_METHOD_STORAGE_KEY, attemptMethod);
			}
		}
	} catch {
		// sessionStorage can throw; still try to fire from this emission.
	}

	let attemptMarked = false;
	try {
		attemptMarked =
			deps.storage.getItem(SIGN_UP_ATTEMPT_STORAGE_KEY) === "1" ||
			shouldMarkSignUpAttempt(snapshot);
	} catch {
		attemptMarked = shouldMarkSignUpAttempt(snapshot);
	}

	let payload = completedSignUpEvent(snapshot, attemptMarked);
	if (!payload) return false;
	if (!payload.method) {
		try {
			const storedMethod = deps.storage.getItem(
				SIGN_UP_ATTEMPT_METHOD_STORAGE_KEY,
			);
			if (storedMethod) {
				payload = { ...payload, method: storedMethod };
			}
		} catch {
			// Keep the payload without method rather than drop the conversion.
		}
	}
	try {
		if (deps.storage.getItem(SIGN_UP_EVENT_STORAGE_KEY) === "1") return false;
		deps.track(payload);
		deps.storage.setItem(SIGN_UP_EVENT_STORAGE_KEY, "1");
		return true;
	} catch {
		deps.track(payload);
		return true;
	}
}
